// web/src/routes/owners/Detail.jsx
// Owner detail — editable contact + birthday + barn-scoped notes, plus the
// horses this owner is on. Assigning/unlinking horses reuses the horse-side
// endpoints (POST/DELETE /horses/:horseId/owners). Owner/manager only.

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import InfoOnlyBadge from '../../components/ui/InfoOnlyBadge';
import { isRealEmail } from '../../lib/email';
import { useAuth } from '../../lib/auth';
import { formatPhoneInput, isCompletePhone } from '../../lib/phone';

export default function OwnerDetail() {
  const { id } = useParams();
  const { activeRole } = useAuth();
  const canManage = activeRole === 'owner' || activeRole === 'manager';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['owner', id],
    queryFn: () => api.get(`/owners/${id}`),
    enabled: canManage,
  });

  if (!canManage) {
    return <div className="p-4 text-ink-500">Owner profiles are for barn staff.</div>;
  }
  if (isLoading) return <div className="p-4 text-ink-400">Loading…</div>;
  if (isError || !data?.owner) {
    return (
      <div className="p-4">
        <p className="text-red-600">Owner not found.</p>
        <Link to="/owners" className="text-ink-800 underline">Back to owners</Link>
      </div>
    );
  }

  const o = data.owner;

  return (
    <div className="max-w-2xl mx-auto p-4">
      <Link to="/owners" className="text-sm text-ink-500 hover:text-ink-800">← Owners</Link>
      <div className="mt-2 mb-6 flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{o.name || o.email}</h1>
        {!isRealEmail(o.email) && <InfoOnlyBadge />}
      </div>

      <ContactCard ownerId={id} owner={o} />
      <HorsesCard ownerId={id} owned={o.horses ?? []} />
      <RiderLinkCard ownerId={id} riderPersonId={o.riderPersonId} ownerName={o.name || o.email} />
    </div>
  );
}

function ContactCard({ ownerId, owner }) {
  const qc = useQueryClient();
  const [name, setName] = useState(owner.name ?? '');
  const [email, setEmail] = useState(owner.email ?? '');
  const [phone, setPhone] = useState(owner.phone ? formatPhoneInput(owner.phone) : '');
  const [birthday, setBirthday] = useState(owner.birthday ?? '');
  const [notes, setNotes] = useState(owner.notes ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/owners/${ownerId}`, {
        name: name.trim(),
        // Only send the email when it's actually editable, so a locked account
        // can never be nudged into a 409 by an unchanged value.
        ...(owner.emailEditable ? { email: email.trim() } : {}),
        phone: phone.trim() || null,
        birthday: birthday || null,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['owner', ownerId] });
      qc.invalidateQueries({ queryKey: ['owners'] });
    },
  });

  const phoneOk = phone === '' || isCompletePhone(phone);
  const emailOk = !owner.emailEditable || (email.trim().includes('@') && email.trim().length > 3);
  const canSave = name.trim() && phoneOk && emailOk && !save.isPending;

  return (
    <section className="card p-5 space-y-4">
      <h2 className="font-medium">Contact</h2>

      <Field label="Name" htmlFor="name">
        <input id="name" className="input w-full" value={name} maxLength={120}
          onChange={(e) => setName(e.target.value)} />
      </Field>

      <Field label="Email" htmlFor="email">
        {owner.emailEditable ? (
          <>
            <input id="email" className="input w-full" type="email" value={email}
              onChange={(e) => setEmail(e.target.value)} />
            <p className="mt-1 text-xs text-ink-400">
              Editable until they set a password. This is where their invite goes.
            </p>
          </>
        ) : (
          <>
            <div className="input w-full bg-ink-50 text-ink-500">{owner.email || '—'}</div>
            <p className="mt-1 text-xs text-ink-400">
              They’ve set a password, so this is their login — only they can change it.
            </p>
          </>
        )}
      </Field>

      <Field label="Phone" optional htmlFor="phone">
        <input id="phone" className="input w-full" type="tel" inputMode="numeric" value={phone}
          placeholder="(555) 555-5555"
          onChange={(e) => setPhone(formatPhoneInput(e.target.value))} />
        {!phoneOk && <p className="mt-1 text-xs text-amber-600">Enter a 10-digit phone number.</p>}
      </Field>

      <Field label="Birthday" optional htmlFor="birthday">
        <input id="birthday" className="input w-full" type="date" value={birthday}
          onChange={(e) => setBirthday(e.target.value)} />
      </Field>

      <Field label="Notes" optional htmlFor="notes">
        <textarea id="notes" className="input w-full" rows={4} value={notes}
          placeholder="Anything worth remembering about this owner."
          onChange={(e) => setNotes(e.target.value)} />
      </Field>

      {save.isError && <p className="text-sm text-red-600">{friendlyError(save.error)}</p>}
      {save.isSuccess && !save.isPending && <p className="text-sm text-emerald-600">Saved.</p>}

      <div>
        <button className="btn-primary" disabled={!canSave} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </section>
  );
}

function HorsesCard({ ownerId, owned }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState('');

  const { data: horsesData } = useQuery({
    queryKey: ['horses'],
    queryFn: () => api.get('/horses'),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['owner', ownerId] });
    qc.invalidateQueries({ queryKey: ['owners'] });
  };

  const assign = useMutation({
    mutationFn: (horseId) => api.post(`/horses/${horseId}/owners`, { userId: ownerId }),
    onSuccess: () => {
      setSelected('');
      invalidate();
    },
  });

  const unlink = useMutation({
    mutationFn: (horseId) => api.del(`/horses/${horseId}/owners/${ownerId}`),
    onSuccess: invalidate,
  });

  const ownedIds = new Set(owned.map((h) => h.id));
  const assignable = (horsesData?.horses ?? []).filter((h) => !ownedIds.has(h.id));

  return (
    <section className="card p-5 mt-4">
      <h2 className="font-medium mb-3">Horses</h2>

      {owned.length === 0 ? (
        <p className="text-sm text-ink-400">Not on any horses yet.</p>
      ) : (
        <ul className="divide-y divide-ink-100">
          {owned.map((h) => (
            <li key={h.id} className="py-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <Link to={`/horses/${h.id}`} className="font-medium text-ink-900 hover:underline">
                  {h.name}
                </Link>
                {h.role === 'lessee' && (
                  <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                    Lessee
                  </span>
                )}
                {/* A share is only meaningful for an owner. */}
                {(h.billingContact ||
                  (h.role !== 'lessee' && h.ownershipPct != null && h.ownershipPct !== 100)) && (
                  <span className="ml-2 text-xs text-ink-500">
                    {h.billingContact ? 'Billing' : ''}
                    {h.billingContact &&
                    h.role !== 'lessee' &&
                    h.ownershipPct != null &&
                    h.ownershipPct !== 100
                      ? ' · '
                      : ''}
                    {h.role !== 'lessee' && h.ownershipPct != null && h.ownershipPct !== 100
                      ? `${h.ownershipPct}%`
                      : ''}
                  </span>
                )}
              </div>
              <button
                onClick={() => {
                  if (confirm(`Remove ${h.name} from this owner?`)) unlink.mutate(h.id);
                }}
                disabled={unlink.isPending}
                className="text-ink-300 hover:text-red-600 shrink-0"
                aria-label="Remove horse"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex items-center gap-2">
        <select
          className="input flex-1"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          <option value="">Add to a horse…</option>
          {assignable.map((h) => (
            <option key={h.id} value={h.id}>
              {h.name}
              {h.stall ? ` (stall ${h.stall})` : ''}
            </option>
          ))}
        </select>
        <button
          className="btn-primary"
          disabled={!selected || assign.isPending}
          onClick={() => assign.mutate(selected)}
        >
          {assign.isPending ? 'Adding…' : 'Add'}
        </button>
      </div>

      {assign.isError && (
        <p className="mt-2 text-sm text-red-600">Couldn’t add to that horse. Try again.</p>
      )}
    </section>
  );
}

function RiderLinkCard({ ownerId, riderPersonId, ownerName }) {
  const qc = useQueryClient();

  const makeRider = useMutation({
    mutationFn: () => api.post('/people', { userId: ownerId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['owner', ownerId] });
      qc.invalidateQueries({ queryKey: ['people'] });
    },
  });

  return (
    <section className="card p-5 mt-4">
      <h2 className="font-medium mb-2">Rider</h2>
      {riderPersonId ? (
        <p className="text-sm text-ink-600">
          {ownerName} is also a rider.{' '}
          <Link to={`/riders/${riderPersonId}`} className="font-medium text-ink-900 hover:underline">
            View rider →
          </Link>
        </p>
      ) : (
        <>
          <p className="text-sm text-ink-500 mb-3">
            Add this owner to the rider roster so they show up on the weekly schedule. Their
            contact stays managed here.
          </p>
          <button className="btn-primary" disabled={makeRider.isPending} onClick={() => makeRider.mutate()}>
            {makeRider.isPending ? 'Adding…' : 'Also a rider'}
          </button>
          {makeRider.isError && (
            <p className="mt-2 text-sm text-red-600">Couldn’t add as a rider. Try again.</p>
          )}
        </>
      )}
    </section>
  );
}

function Field({ label, htmlFor, optional, children }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-ink-700 mb-1">
        {label}
        {optional && <span className="font-normal text-ink-400"> (optional)</span>}
      </label>
      {children}
    </div>
  );
}

function friendlyError(err) {
  const code = err?.body?.error || err?.message || '';
  switch (code) {
    case 'missing_name':
      return 'Name is required.';
    case 'no_fields':
      return 'Nothing to save.';
    case 'forbidden':
      return 'Only an owner or manager can edit owners.';
    case 'not_found':
      return 'That owner no longer exists.';
    case 'email_locked':
      return 'They’ve already set a password — only they can change their own email.';
    case 'email_taken':
      return 'Another account already uses that email.';
    case 'invalid_email':
      return 'Enter a valid email address.';
    default:
      return 'Could not save. Try again.';
  }
}

